import { faker } from '@faker-js/faker'

import {
  CommunityCommentView,
  CommunityMedicalTipView,
  CommunityRecommendationsView,
  CommunityView,
  DocumentCommentView,
  DocumentStatus,
  MemberDoctorView,
  MemberType,
  MemberView,
  ReportCommunityContentParam,
  ReportEntityType,
  ReportReason,
} from '../../contracts/community'
import { PageSortField, PageSortOrder, Pagination, SpringPaginationResult } from '../../src/community'
import { asBuilder } from '../asBuilder'

export const mockCommunityView = asBuilder<CommunityView>(
  (): CommunityView => ({
    id: faker.number.int(),
    category: {
      categoryId: faker.number.int(),
      categoryName: faker.commerce.department(),
      categoryIconUrl: faker.image.url(),
    },
    contents: {
      contents: faker.lorem.paragraphs(),
      originContents: faker.lorem.paragraphs(),
    },
    photos: Array.from({ length: 5 }, () => faker.image.url()),
    statistics: {
      viewCount: faker.number.int(),
      thumbUpCount: faker.number.int(),
      commentCount: faker.number.int(),
      subscriberCount: faker.number.int(),
    },
    writer: mockCommunityMemberView(),
    blindHistory: {
      id: faker.number.int(),
      requesterName: faker.string.alpha(),
      requestTime: faker.date.past().toISOString(),
      restoreRequestTime: faker.date.past().toISOString(),
      restoredTime: faker.date.past().toISOString(),
      restoreDeadlineTime: faker.date.past().toISOString(),
    },
    personalize: {
      hasThumbUp: faker.datatype.boolean(),
      hasSubscribed: faker.datatype.boolean(),
    },
    language: faker.string.alpha(),
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.past().toISOString(),
  }),
)

export const mockCommunityCommentsView = asBuilder<CommunityCommentView>(
  (): CommunityCommentView => ({
    id: faker.number.int(),
    communityId: faker.number.int(),
    contents: {
      contents: faker.lorem.paragraphs(),
      originContents: faker.lorem.paragraphs(),
    },
    replyCommentId: faker.number.int(),
    statistics: {
      thumbUpCount: faker.number.int(),
    },
    callee: mockCommunityMemberView(),
    writer: mockCommunityMemberView(),
    blindHistory: {
      id: faker.number.int(),
      requesterName: faker.lorem.word(),
      requestTime: faker.date.past().toISOString(),
      restoreRequestTime: faker.date.past().toISOString(),
      restoredTime: faker.date.past().toISOString(),
      restoreDeadlineTime: faker.date.past().toISOString(),
    },
    personalize: {
      hasThumbUp: faker.datatype.boolean(),
    },
    language: faker.lorem.word(),
    deleted: faker.datatype.boolean(),
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.past().toISOString(),
  }),
)

const mockCommunityMemberView = asBuilder<MemberView>(
  (): MemberView => ({
    memberId: faker.number.int(),
    doctor: mockMemberDoctorView(),
    country: faker.lorem.word(),
    type: faker.helpers.enumValue(MemberType),
    profileUrl: faker.image.avatar(),
    nickName: faker.internet.userName(),
    level: {
      level: faker.number.int({ min: 1, max: 10 }),
      activist: faker.datatype.boolean(),
      knowledgeTopPercent: faker.number.float({ min: 0, max: 100 }),
      informTopPercent: faker.number.float({ min: 0, max: 100 }),
      helpfulTopPercent: faker.number.float({ min: 0, max: 100 }),
    },
    engagementType: faker.lorem.word(),
  }),
)

export const mockCommunityCommentAboutLike = asBuilder<DocumentCommentView>(
  (): DocumentCommentView => ({
    id: faker.number.int(),
    createTime: faker.number.int(),
    updateTime: faker.number.int(),
    status: faker.helpers.enumValue(DocumentStatus),
    calleeId: faker.helpers.arrayElement([faker.number.int(), null]),
    calleeNickName: faker.helpers.arrayElement([faker.string.sample(), null]),
    calleeLevel: faker.helpers.arrayElement([faker.number.int(), null]),
    calleeFake: faker.datatype.boolean(),
    replyCommentId: faker.helpers.arrayElement([faker.number.int(), null]),
    contents: faker.string.sample(),
    thumbUpCount: faker.number.int(),
    commentCount: faker.number.int(),
    reportCount: faker.number.int(),
    edited: faker.datatype.boolean(),
    isBest: faker.datatype.boolean(),
    blindRestoreTime: faker.helpers.arrayElement([faker.number.int(), null]),
    lang: faker.helpers.arrayElement(['ko', 'ja', faker.string.sample(), null]),
    translateResult: faker.helpers.arrayElement([{ contents: faker.string.sample() }, null]),
    writerCountry: faker.string.sample(),
    hasThumbUp: faker.datatype.boolean(),
    hasReported: faker.helpers.arrayElement([faker.datatype.boolean(), null]),
    skipped: faker.helpers.arrayElement([faker.datatype.boolean(), null]),
    writerNickName: faker.string.sample(),
    documentWriterId: faker.number.int(),
    documentId: faker.number.int(),
    writerId: faker.number.int(),
    writerFake: faker.datatype.boolean(),
    writerDoctorId: faker.helpers.arrayElement([faker.number.int(), null]),
    calleeDoctorId: faker.helpers.arrayElement([faker.number.int(), null]),
    writerLevel: faker.number.int(),
    writerProfile: faker.string.sample(),
    writerName: faker.string.sample(),
    localTextFieldName: faker.string.sample(),
    blindRequestDTO: null,
  }),
)

const mockMemberDoctorView = asBuilder<MemberDoctorView>(
  (): MemberDoctorView => ({
    memberId: faker.number.int(),
    doctorId: faker.number.int(),
    name: faker.lorem.word(),
    hospitalId: faker.number.int(),
    hospitalName: faker.lorem.word(),
    typeName: faker.lorem.word(),
  }),
)

export const mockCommunityMedicalTipView = asBuilder<CommunityMedicalTipView>(
  (): CommunityMedicalTipView => ({
    id: faker.number.int(),
    status: faker.helpers.arrayElement(['REMOVED', 'PENDING', 'APPROVED']),
    title: faker.lorem.sentence(),
    contents: faker.lorem.paragraph(),
    language: faker.location.countryCode(),
    personaName: faker.person.fullName(),
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.past().toISOString(),
    publishedAt: faker.date.past().toISOString(),
    thumbnails: [faker.image.url()],
  }),
)

export const mockReportCommunityContentParam = asBuilder<ReportCommunityContentParam>(
  (): ReportCommunityContentParam => ({
    entityType: faker.helpers.enumValue(ReportEntityType),
    entityId: faker.number.int(),
    reason: faker.helpers.enumValue(ReportReason),
    memo: faker.helpers.arrayElement([faker.lorem.paragraph(), undefined]),
    isSkip: faker.datatype.boolean(),
  }),
)

export const mockCommunityRecommendationsView = asBuilder<CommunityRecommendationsView>(
  (): CommunityRecommendationsView => ({
    communityId: faker.number.int(),
    contents: faker.lorem.paragraphs(),
    totalCommentCount: faker.number.int(),
    doctorCommentWritten: faker.datatype.boolean(),
  }),
)

export const mockPagination = asBuilder<Pagination>(
  (): Pagination => ({
    pageIndex: faker.number.int(),
    pageSize: faker.number.int(),
    pageSorts: [
      {
        pageSortField: faker.helpers.enumValue(PageSortField),
        pageSortOrder: faker.helpers.enumValue(PageSortOrder),
      },
    ],
  }),
)

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const mockSpringPaginationResult = <T>(result: T) => {
  const mock = asBuilder<SpringPaginationResult<T>>(
    (): SpringPaginationResult<T> => ({
      content: [result],
      contents: [result],
      pageable: {
        sort: {
          sorted: faker.datatype.boolean(),
          unsorted: faker.datatype.boolean(),
          empty: faker.datatype.boolean(),
        },
        offset: faker.number.int(),
        pageNumber: faker.number.int(),
        pageSize: faker.number.int(),
        paged: faker.datatype.boolean(),
        unpaged: faker.datatype.boolean(),
      },
      last: faker.datatype.boolean(),
      totalPages: faker.number.int(),
      totalElements: faker.number.int(),
      sort: {
        sorted: faker.datatype.boolean(),
        unsorted: faker.datatype.boolean(),
        empty: faker.datatype.boolean(),
      },
      first: faker.datatype.boolean(),
      size: faker.number.int(),
      number: faker.number.int(),
      numberOfElements: faker.number.int(),
      empty: faker.datatype.boolean(),
    }),
  )

  return mock()
}
